'use client'

import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { PREMIERE_SECTION, SECTIONS } from '@/lib/certificat'

/**
 * Les repères de lecture : la jauge de défilement, la navigation par section,
 * et le retour en haut.
 *
 * ⭐ SUR UNE PAGE LONGUE, SAVOIR OÙ L'ON EST FAIT PARTIE DU CONTENU. Un
 * certificat se parcourt, on y revient, on cherche l'article huit ou le numéro
 * de pièce. Sans repère, il faut refaire tout le trajet à chaque fois, et une
 * page de prestige qui se parcourt mal cesse d'être une pièce pour devenir un
 * mur de texte.
 *
 * ⛔⛔ LA POSITION EST MESURÉE, JAMAIS OBSERVÉE. 🟢 Mesuré le 2026-09-07 : un
 * `IntersectionObserver` n'a rendu la main aucune fois dans un navigateur au
 * rendu piloté à la demande. Ici l'enjeu est moindre, mais la règle tient : ce
 * qui doit répondre se lit, ce qui optimise s'observe.
 *
 * ⭐ ET TOUT MARCHE SANS JAVASCRIPT. La navigation est faite de vraies ancres :
 * sans script, elle mène quand même à la bonne section, et le bouton de retour
 * en haut est un lien vers le haut de page. Le script n'ajoute que la surbrillance
 * et la jauge.
 */

/** En dessous, on ne montre pas encore le retour en haut : rien n'est loin. */
const SEUIL_RETOUR = 700

export function Reperes() {
  const [actif, setActif] = useState<string>(PREMIERE_SECTION)
  const [avancee, setAvancee] = useState(0)
  const [loin, setLoin] = useState(false)
  const [sommaire, setSommaire] = useState(false)
  const enAttente = useRef(false)
  const bouton = useRef<HTMLButtonElement>(null)

  /*
   * Les trois façons de refermer le sommaire, et elles sont toutes attendues :
   * choisir une entrée, appuyer sur Échap, ou toucher ailleurs.
   *
   * ⭐ Échap rend le focus au bouton. Sans ce retour, la tabulation repartirait
   * du début du document après la fermeture : au clavier, on croit avoir été
   * éjecté de la page.
   *
   * ⛔ Le clic extérieur écoute `pointerdown` et NON `click`, et il ignore le
   * bouton lui-même. Sans cette exclusion, appuyer sur le bouton pour refermer
   * déclencherait d'abord la fermeture par l'extérieur, puis la bascule du
   * bouton juste après : le panneau se rouvrirait aussitôt, et le bouton
   * passerait pour cassé.
   *
   * ⚠️ Le focus ne revient au bouton QUE pour Échap. Le ramener aussi après un
   * clic extérieur déplacerait le curseur sous le doigt de quelqu'un qui venait
   * justement de vouloir aller ailleurs.
   */
  useEffect(() => {
    if (!sommaire) return
    const arret = new AbortController()

    window.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return
        setSommaire(false)
        bouton.current?.focus()
      },
      { signal: arret.signal },
    )

    document.addEventListener(
      'pointerdown',
      (e) => {
        const cible = e.target as HTMLElement | null
        if (cible?.closest('.reperes, .sommaire-bouton')) return
        setSommaire(false)
      },
      { signal: arret.signal },
    )

    return () => arret.abort()
  }, [sommaire])

  useEffect(() => {
    const lire = () => {
      enAttente.current = false
      const haut = window.scrollY
      const course = document.documentElement.scrollHeight - window.innerHeight
      setAvancee(course > 0 ? Math.min(1, Math.max(0, haut / course)) : 0)
      setLoin(haut > SEUIL_RETOUR)

      /* ⚠️ La section courante est celle dont le haut est passé au-dessus du
         tiers supérieur de l'écran : prendre celle qui « touche » l'écran ferait
         changer le repère deux fois par section, une à l'entrée et une à la
         sortie, et la navigation clignoterait. */
      let courante: string = PREMIERE_SECTION
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id)
        if (el && el.getBoundingClientRect().top <= window.innerHeight / 3) courante = s.id
      }
      setActif(courante)
    }

    /* ⚠️ Une seule lecture par image : le défilement émet bien plus souvent que
       l'écran ne se rafraîchit, et mesurer huit sections à chaque événement
       ferait payer la fluidité qu'on cherche à donner. */
    const surDefilement = () => {
      if (enAttente.current) return
      enAttente.current = true
      requestAnimationFrame(lire)
    }

    lire()
    window.addEventListener('scroll', surDefilement, { passive: true })
    window.addEventListener('resize', surDefilement, { passive: true })
    return () => {
      window.removeEventListener('scroll', surDefilement)
      window.removeEventListener('resize', surDefilement)
    }
  }, [])

  return (
    <>
      {/* ⚠️ `aria-hidden` : une jauge de défilement répète une information que
          le navigateur donne déjà, et qu'une synthèse vocale connaît par
          ailleurs. L'annoncer une seconde fois est du bruit, pas de
          l'accessibilité. */}
      <div className="jauge" aria-hidden>
        <span style={{ transform: `scaleX(${avancee})` }} />
      </div>

      {/* ⭐ LE MÊME BALISAGE POUR LES DEUX TAILLES D'ÉCRAN. Sur grand écran il
          vit en permanence dans la marge, sur téléphone c'est un panneau qu'on
          ouvre. Deux navigations séparées finiraient par ne plus dire la même
          chose, et c'est celle du téléphone qui mentirait en premier. */}
      <nav
        id="sommaire"
        className="reperes"
        data-ouvert={sommaire ? 'oui' : undefined}
        aria-label="Sections du certificat"
      >
        <ol>
          {SECTIONS.map((s, rang) => (
            /* ⭐ Le rang sert au décalage de la cascade à l'ouverture. Il est
               compté À L'ENVERS : la dernière entrée est celle qui touche le
               bouton, donc c'est par elle que le panneau se déplie. Compté à
               l'endroit, l'animation partirait du coin le plus éloigné du
               doigt. ⚠️ C'est le composant qui compte, pas `nth-child` : le CSS
               figerait le décalage au nombre de sections d'aujourd'hui. */
            <li
              key={s.id}
              style={{ '--rang': SECTIONS.length - 1 - rang } as CSSProperties}
            >
              <a
                href={`#${s.id}`}
                data-ancre
                onClick={() => setSommaire(false)}
                /* ⭐ `aria-current="true"` et non une simple classe : la
                   surbrillance est une information, pas une décoration, et une
                   synthèse vocale doit pouvoir dire « ici ». */
                aria-current={actif === s.id ? 'true' : undefined}
              >
                {s.titre}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* ⛔ Sur téléphone, la marge droite n'existe pas : le sommaire devient un
          panneau. Le supprimer aurait été plus simple et plus faux, c'est
          justement sur un petit écran qu'on a besoin de revenir à l'article
          huit sans refaire défiler toute la page. */}
      <button
        ref={bouton}
        type="button"
        className="bouton-flottant sommaire-bouton"
        aria-expanded={sommaire}
        aria-controls="sommaire"
        aria-label={sommaire ? 'Fermer le sommaire' : 'Ouvrir le sommaire'}
        onClick={() => setSommaire((v) => !v)}
      >
        <span aria-hidden />
        <span aria-hidden />
        <span aria-hidden />
      </button>

      {/* ⭐ Un LIEN vers le haut de page, pas un bouton : il fonctionne sans
          script, il s'ouvre dans un nouvel onglet si on le demande, et il porte
          une vraie destination. Un bouton qui appelle `scrollTo` ne sait rien
          faire de tout ça.
          ⚠️ `inert` quand il est loin : un élément invisible mais focalisable
          est un piège au clavier, on tabule dans le vide. */}
      <a
        href="#haut"
        data-ancre
        className="bouton-flottant retour-haut"
        aria-label="Revenir en haut de la page"
        hidden={!loin}
      >
        <span aria-hidden>
          {/* ⭐ Un tracé et pas un « ↑ » : l'encre d'un glyphe est centrée par sa
              fonte, pas par la mise en page. Voir `certificat.css`. */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5" />
            <path d="M6 11l6-6 6 6" />
          </svg>
        </span>
      </a>
    </>
  )
}
